import { useEffect, useState, type CSSProperties } from 'react';

const skeletonKinds = ['candles', 'bars', 'kagi'] as const;

type SkeletonKind = (typeof skeletonKinds)[number];

interface ChartLoadingSkeletonProps {
  className?: string;
  label?: string;
}

interface MainLoadingViewProps {
  label?: string;
}

export function MainLoadingView({ label = 'Loading' }: MainLoadingViewProps) {
  return (
    <div className="main-loading-view" role="status" aria-label={label}>
      <div className="main-loading-chart" aria-hidden="true">
        <ChartLoadingSkeleton />
      </div>
      <span className="main-loading-text">Chartify</span>
    </div>
  );
}

export function ChartLoadingSkeleton({ className = '', label = 'Loading candles' }: ChartLoadingSkeletonProps) {
  const [kind, setKind] = useState<SkeletonKind>(() => randomSkeletonKind());
  const candles = Array.from({ length: 34 }, (_, index) => {
    const wave = Math.sin(index * 0.58) * 9 + Math.sin(index * 0.19) * 18;
    const center = Math.min(72, Math.max(28, 50 - wave));
    const wickHeight = 14 + ((index * 7) % 19);
    const bodyHeight = 7 + ((index * 5) % 15);
    return {
      wickTop: Math.max(8, center - wickHeight / 2),
      wickHeight,
      bodyTop: Math.max(10, center - bodyHeight / 2),
      bodyHeight,
    };
  });
  const bars = Array.from({ length: 30 }, (_, index) => {
    const wave = Math.sin(index * 0.45) * 13 + Math.cos(index * 0.23) * 10;
    const center = Math.min(70, Math.max(30, 52 - wave));
    const height = 18 + ((index * 11) % 22);
    return {
      top: Math.max(8, center - height / 2),
      height,
    };
  });

  useEffect(() => {
    const timer = window.setInterval(() => {
      setKind((current) => nextSkeletonKind(current));
    }, 1400);
    return () => window.clearInterval(timer);
  }, []);

  if (kind === 'kagi') {
    return (
      <div className={`chart-loading-skeleton kagi ${className}`.trim()} aria-label={label}>
        <svg viewBox="0 0 220 130" aria-hidden="true">
          <path d="M16 92 H38 V72 H58 V88 H82 V48 H104 V64 H126 V34 H150 V54 H174 V24 H204" />
        </svg>
      </div>
    );
  }

  if (kind === 'bars') {
    return (
      <div className={`chart-loading-skeleton bars ${className}`.trim()} aria-label={label}>
        {bars.map((bar, index) => (
          <span
            key={index}
            style={{
              '--bar-top': `${bar.top}%`,
              '--bar-height': `${bar.height}%`,
            } as CSSProperties}
          >
            <i />
            <b />
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className={`chart-loading-skeleton candles ${className}`.trim()} aria-label={label}>
      <div className="chart-loading-skeleton-grid" />
      {candles.map((candle, index) => (
        <span
          key={index}
          style={{
            '--wick-top': `${candle.wickTop}%`,
            '--wick-height': `${candle.wickHeight}%`,
            '--body-top': `${candle.bodyTop}%`,
            '--body-height': `${candle.bodyHeight}%`,
          } as CSSProperties}
        >
          <i />
          <b />
        </span>
      ))}
    </div>
  );
}

function randomSkeletonKind(): SkeletonKind {
  return skeletonKinds[Math.floor(Math.random() * skeletonKinds.length)] ?? 'candles';
}

function nextSkeletonKind(current: SkeletonKind): SkeletonKind {
  const next = randomSkeletonKind();
  if (next === current) {
    const index = skeletonKinds.indexOf(current);
    return skeletonKinds[(index + 1) % skeletonKinds.length] ?? 'candles';
  }
  return next;
}
